import { useState, useRef, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField, Box, Typography, IconButton, Button } from '@mui/material';
import { fetchWithAuth } from '../../../api/backendApi';
import MicIcon from '@mui/icons-material/Mic';
import StopIcon from '@mui/icons-material/Stop';
import CloseIcon from '@mui/icons-material/Close';

function downsampleToInt16(input, inputSampleRate, outputSampleRate) {
  if (!input || input.length === 0) return null;

  if (outputSampleRate === inputSampleRate) {
    const buffer = new Int16Array(input.length);
    for (let i = 0; i < input.length; i++) {
      let s = Math.max(-1, Math.min(1, input[i]));
      buffer[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
    }
    return buffer;
  }

  const sampleRateRatio = inputSampleRate / outputSampleRate;
  const newLength = Math.round(input.length / sampleRateRatio);
  const result = new Int16Array(newLength);
  let offsetResult = 0;
  let offsetBuffer = 0;

  while (offsetResult < newLength) {
    const nextOffsetBuffer = Math.round((offsetResult + 1) * sampleRateRatio);
    let accum = 0;
    let count = 0;

    for (let i = Math.floor(offsetBuffer); i < nextOffsetBuffer && i < input.length; i++) {
      accum += input[i];
      count++;
    }

    let sample = count > 0 ? accum / count : 0;
    sample = Math.max(-1, Math.min(1, sample));
    result[offsetResult] = sample < 0 ? sample * 0x8000 : sample * 0x7fff;

    offsetResult++;
    offsetBuffer = nextOffsetBuffer;
  }

  return result;
}

export default function RtpPagingAdvancedDialog({ open, onClose, presetZoneId, presetZoneName }) {
  const [targetAgent, setTargetAgent] = useState('default'); // Backend will auto-select an online agent
  const [deviceIp, setDeviceIp] = useState('');
  const [multicastGroup, setMulticastGroup] = useState('239.0.0.1');
  const [port, setPort] = useState('5000');
  const [ttl, setTtl] = useState('1');
  const [agentHost, setAgentHost] = useState('');
  const [zoneId, setZoneId] = useState(presetZoneId || '');
  const [zoneName, setZoneName] = useState(presetZoneName || '');
  const [isPaging, setIsPaging] = useState(false);
  const [status, setStatus] = useState('Idle');
  const wsRef = useRef(null);
  const audioContextRef = useRef(null);
  const processorRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const sessionIdRef = useRef(null);
  const lastStartedAtRef = useRef(0);

  const buildOverridePayload = () => {
    const override = {};
    if (multicastGroup.trim()) override.group = multicastGroup.trim();
    if (port.trim()) override.port = Number(port.trim());
    if (ttl.trim()) override.ttl = Number(ttl.trim());
    if (agentHost.trim()) override.agentHost = agentHost.trim();
    if (deviceIp.trim()) override.deviceIp = deviceIp.trim();
    return override;
  };

  const stopAudio = () => {
    if (processorRef.current) {
      try {
        processorRef.current.disconnect();
      } catch (e) {
        console.error('[RtpPagingAdvancedDialog] Error disconnecting audio processor', e);
      }
      processorRef.current.onaudioprocess = null;
      processorRef.current = null;
    }

    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track) => {
        try {
          track.stop();
        } catch (e) {
          console.error('[RtpPagingAdvancedDialog] Error stopping media track', e);
        }
      });
      mediaStreamRef.current = null;
    }

    if (audioContextRef.current) {
      try {
        audioContextRef.current.close();
      } catch (e) {
        console.error('[RtpPagingAdvancedDialog] Error closing AudioContext', e);
      }
      audioContextRef.current = null;
    }
  };

  const startAudioCapture = async () => {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      console.error('[RtpPagingAdvancedDialog] getUserMedia is not supported in this browser');
      setStatus('Browser does not support microphone capture');
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
        },
      });
      mediaStreamRef.current = stream;

      const AudioContextClass = window.AudioContext || window.webkitAudioContext;
      const audioContext = new AudioContextClass();
      audioContextRef.current = audioContext;

      const source = audioContext.createMediaStreamSource(stream);
      const processor = audioContext.createScriptProcessor(4096, 1, 1);
      processorRef.current = processor;
      const targetSampleRate = 8000;

      processor.onaudioprocess = (event) => {
        const channelData = event.inputBuffer.getChannelData(0);
        const ws = wsRef.current;
        if (!ws || ws.readyState !== WebSocket.OPEN) {
          return;
        }

        const pcm16 = downsampleToInt16(channelData, audioContext.sampleRate, targetSampleRate);
        if (pcm16 && ws.readyState === WebSocket.OPEN) {
          try {
            ws.send(pcm16.buffer);
          } catch (e) {
            console.error('[RtpPagingAdvancedDialog] Error sending audio chunk', e);
          }
        }
      };

      source.connect(processor);
      processor.connect(audioContext.destination);
      setStatus('Streaming microphone audio to agent...');
    } catch (err) {
      console.error('[RtpPagingAdvancedDialog] Microphone error', err);
      setStatus('Microphone error or permission denied');
    }
  };

  const handleStartStop = async () => {
    if (!isPaging) {
      try {
        if (!zoneId.trim()) {
          setStatus('A paging zone is required (configure zones in Admin > Tenants > Paging)');
          return;
        }

        const rawAgent = (targetAgent || '').trim();
        if (!rawAgent) {
          setStatus('Target Agent ID is required');
          return;
        }
        setStatus('Requesting paging session from backend...');
        console.log('[RtpPagingAdvancedDialog] Calling backend to start paging session');

        let target = rawAgent;
        if (target && !target.startsWith('agent-')) {
          target = `agent-${target}`;
        }

        const body = {
          target,
          displayLabel: rawAgent || target,
          zoneId: zoneId.trim(),
          override: buildOverridePayload(),
        };

        const resp = await fetchWithAuth('/api/paging/session/start', 'POST', body);
        if (!resp?.success) {
          console.error('[RtpPagingAdvancedDialog] Backend paging start failed', resp);
          setStatus(resp?.error || 'Backend paging start failed');
          return;
        }
        const { sessionId, wsUrl } = resp;
        sessionIdRef.current = sessionId;
        console.log('[RtpPagingAdvancedDialog] Backend returned session', { sessionId, wsUrl });

        const socket = new WebSocket(wsUrl);
        wsRef.current = socket;
        setStatus('Connecting to agent...');
        console.log('[RtpPagingAdvancedDialog] Opening WebSocket', wsUrl);

        socket.onopen = () => {
          console.log('[RtpPagingAdvancedDialog] WebSocket connected');
          lastStartedAtRef.current = Date.now();
          setIsPaging(true);
          setStatus('Connected; starting microphone...');
          startAudioCapture();
        };

        socket.onerror = (event) => {
          console.error('[RtpPagingAdvancedDialog] WebSocket error', event);
          setStatus('Error connecting to agent');
        };

        socket.onclose = (event) => {
          const closedAt = Date.now();
          const startedAt = lastStartedAtRef.current || 0;
          const liveMs = startedAt > 0 ? Math.max(0, closedAt - startedAt) : 0;
          const closeDetails = {
            sessionId: sessionIdRef.current,
            code: event.code,
            reason: event.reason || '(none)',
            wasClean: event.wasClean,
            readyState: socket.readyState,
            liveMs,
          };
          console.warn('[RtpPagingAdvancedDialog] WebSocket closed', closeDetails);
          stopAudio();
          wsRef.current = null;
          setIsPaging(false);
          const reasonText = event.reason ? ` (${event.reason})` : '';
          setStatus(`Disconnected (code ${event.code})${reasonText}`);
        };
      } catch (err) {
        console.error('[RtpPagingAdvancedDialog] Failed to start paging session via backend', err);
        setStatus(err?.message || 'Failed to start paging session');
      }
    } else {
      if (wsRef.current) {
        try {
          wsRef.current.close();
        } catch (e) {
          console.error('[RtpPagingAdvancedDialog] Error closing WebSocket', e);
        }
        wsRef.current = null;
      }
      stopAudio();
      setIsPaging(false);
      setStatus('Stopped');
      console.log('[RtpPagingAdvancedDialog] Stop paging');

      const sid = sessionIdRef.current;
      sessionIdRef.current = null;
      if (sid) {
        try {
          await fetchWithAuth('/api/paging/session/stop', 'POST', { sessionId: sid }, { silent: true });
          console.log('[RtpPagingAdvancedDialog] Notified backend to stop session', sid);
        } catch (e) {
          console.warn('[RtpPagingAdvancedDialog] Failed to notify backend to stop session', { sessionId: sid, error: e?.message });
        }
      }
    }
  };

  const handleClose = async () => {
    if (wsRef.current) {
      try {
        wsRef.current.close();
      } catch (e) {
        console.error('[RtpPagingAdvancedDialog] Error closing WebSocket on dialog close', e);
      }
      wsRef.current = null;
    }
    stopAudio();
    setIsPaging(false);
    setStatus('Idle');

    const sid = sessionIdRef.current;
    sessionIdRef.current = null;
    if (sid) {
      try {
        await fetchWithAuth('/api/paging/session/stop', 'POST', { sessionId: sid }, { silent: true });
        console.log('[RtpPagingAdvancedDialog] Notified backend to stop session on close', sid);
      } catch (e) {
        console.warn('[RtpPagingAdvancedDialog] Failed to notify backend to stop session on close', { sessionId: sid, error: e?.message });
      }
    }

    onClose?.();
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6" component="span">Manual RTP Paging</Typography>
        <IconButton size="small" onClick={handleClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Specify the target agent and RTP parameters. The agent will return a WebSocket URL and your microphone
          audio will be streamed live to the configured multicast target.
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Target Agent ID"
            size="small"
            helperText="Enter a tenant agent id; will be normalized to the correct agent id"
            value={targetAgent}
            onChange={(e) => setTargetAgent(e.target.value)}
          />

          <TextField
            label="Paging zone"
            size="small"
            helperText="Choose the paging zone configured in Admin > Tenants > Paging"
            value={zoneId}
            onChange={(e) => setZoneId(e.target.value)}
          />

          <TextField
            label="Algo Device IP (optional)"
            size="small"
            helperText="For operator reference; control plane passes this through but RTP uses group/port"
            value={deviceIp}
            onChange={(e) => setDeviceIp(e.target.value)}
          />

          <TextField
            label="Agent Host Override (host:port)"
            size="small"
            helperText="Optional advanced override for the agent WebSocket host; normally leave blank"
            value={agentHost}
            onChange={(e) => setAgentHost(e.target.value)}
          />

          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Multicast Group"
              size="small"
              fullWidth
              value={multicastGroup}
              onChange={(e) => setMulticastGroup(e.target.value)}
            />
            <TextField
              label="Port"
              size="small"
              type="number"
              sx={{ width: 140 }}
              value={port}
              onChange={(e) => setPort(e.target.value)}
            />
          </Box>

          <TextField
            label="TTL"
            size="small"
            type="number"
            sx={{ width: 140 }}
            value={ttl}
            onChange={(e) => setTtl(e.target.value)}
          />

          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
            <IconButton
              onClick={handleStartStop}
              color={isPaging ? 'error' : 'primary'}
              sx={{
                width: 88,
                height: 88,
                borderRadius: '50%',
                boxShadow: 4,
              }}
            >
              {isPaging ? <StopIcon sx={{ fontSize: 40 }} /> : <MicIcon sx={{ fontSize: 40 }} />}
            </IconButton>
            <Typography variant="body2" color={isPaging ? 'error.main' : 'text.secondary'}>
              {isPaging ? 'Paging in progress' : 'Tap mic to start paging'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {status}
            </Typography>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
